/**
 * Browser-side document text extraction.
 *
 * Workers don't have filesystem access, so text extraction happens
 * in the browser before uploading to the Worker API.
 * The Worker receives both the original file (for R2) and extracted text (for chunking).
 */

/**
 * Extract text content from a file in the browser.
 * Supports: .txt, .md, .csv, .json, .docx, .xlsx
 */
export async function extractTextFromFile(file: File): Promise<string> {
  const extension = file.name.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'txt':
    case 'md':
    case 'csv':
      return await file.text();

    case 'json': {
      const jsonText = await file.text();
      try {
        const parsed = JSON.parse(jsonText);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return jsonText;
      }
    }

    case 'docx':
      return await extractDocx(file);

    case 'xlsx':
    case 'xls':
      return await extractExcel(file);

    default:
      // Attempt plain text for unknown types
      return await file.text();
  }
}

async function extractDocx(file: File): Promise<string> {
  // mammoth is browser-compatible
  const mammoth = await import('mammoth');
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
}

async function extractExcel(file: File): Promise<string> {
  // xlsx/SheetJS is browser-compatible
  const XLSX = await import('xlsx');
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });

  const sheets = workbook.SheetNames.map((name: string) => {
    const sheet = workbook.Sheets[name];
    if (!sheet) return '';
    return `--- Sheet: ${name} ---\n${XLSX.utils.sheet_to_csv(sheet)}`;
  });

  return sheets.join('\n\n');
}
